import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { communityThreads } from '@/mocks/home';

function ThreadCard({
  avatar,
  nickname,
  level,
  time,
  content,
  likes,
  replies,
  topic,
}: (typeof communityThreads)[number]) {
  const [liked, setLiked] = useState(false);

  return (
    <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
      <header className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 font-heading text-sm font-bold text-secondary-900">
          {avatar}
        </span>
        <div className="flex flex-1 flex-col">
          <span className="font-label text-sm text-foreground-900">{nickname}</span>
          <span className="mt-0.5 font-label text-[11px] text-foreground-500">
            {level} · {time}
          </span>
        </div>
        <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900">
          {topic}
        </span>
      </header>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">{content}</p>
      <footer className="mt-4 flex items-center gap-4 border-t border-background-200/80 pt-3.5 font-label text-xs">
        <button
          type="button"
          onClick={() => setLiked((prev) => !prev)}
          className={`flex cursor-pointer items-center gap-1.5 transition-colors ${
            liked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
          }`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={liked ? 'ri-heart-3-fill text-sm' : 'ri-heart-3-line text-sm'} />
          </span>
          {liked ? likes + 1 : likes}
        </button>
        <span className="flex items-center gap-1.5 text-foreground-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-chat-3-line text-sm" />
          </span>
          {replies}
        </span>
        <span className="flex items-center gap-1.5 text-foreground-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-share-forward-line text-sm" />
          </span>
          转发
        </span>
      </footer>
    </article>
  );
}

export default function CommunitySection() {
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');

  const handlePost = () => {
    setNotice(draft.trim() ? '讨论区当前为演示状态，正式开放后可发布。' : '写点什么再发布吧。');
  };

  return (
    <section id="community" className="scroll-mt-20 bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 xl:px-10">
        <SectionHeading
          eyebrow="Community"
          title="社区讨论：一起把脉络理清"
          desc="档案不是给结论，而是给讨论的起点。这里汇集读者对事件脉络、信源引用与归类方式的补充和质疑。"
        />

        <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-edit-2-line text-base" />
              </span>
              <input
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="你对这份档案有补充或不同看法？"
                className="w-full rounded-md border border-background-200 bg-background-100 py-3 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
            </div>
            <button
              type="button"
              onClick={handlePost}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              发布讨论
            </button>
          </div>
          {notice ? (
            <p className="mt-3 flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2 text-xs text-secondary-900">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-information-line text-sm" />
              </span>
              {notice}
            </p>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
          {communityThreads.map((thread) => (
            <ThreadCard key={thread.nickname} {...thread} />
          ))}
        </div>
      </div>
    </section>
  );
}